import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import * as XLSX from 'xlsx';
import {
    X, Plus, Check, Trash2, Search, Upload, Download, Loader2,
    AlertTriangle, CheckSquare, Square, Pencil, Hash, FileText, Tag,
    Copy, ClipboardPaste, ArrowUp, ArrowDown, ChevronsUpDown
} from 'lucide-react';

const VIRTUAL_ROW_H = 36;
const VISIBLE_BUFFER = 10;

export default function NMRecordEditor({ open, editingId, initialData, onSave, onClose, onEditIdChange }) {
    // ── Core state ──
    const [nm, setNm] = useState('');
    const [rows, setRows] = useState([]); // [{pn:'', desc:'', id: unique}]
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [editCell, setEditCell] = useState(null); // {idx, field}
    const [editVal, setEditVal] = useState('');
    const [sortCol, setSortCol] = useState(null); // 'pn' | 'desc'
    const [sortDir, setSortDir] = useState('asc');

    // ── Autocomplete state ──
    const [nmSuggestions, setNmSuggestions] = useState([]);
    const [nmSuggestOpen, setNmSuggestOpen] = useState(false);
    const [nmSuggestLoading, setNmSuggestLoading] = useState(false);
    const nmInputRef = useRef(null);
    const suggestDropdownRef = useRef(null);

    const scrollRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const editRef = useRef(null);
    const pnPasteRef = useRef(null);
    const nextId = useRef(1);

    const uid = () => nextId.current++;

    // ── Init from props ──
    useEffect(() => {
        if (!open) return;
        const pns = initialData?.partNumbers || [];
        const descs = initialData?.descriptions || [];
        const maxLen = Math.max(pns.length, descs.length, 0);
        const r = [];
        for (let i = 0; i < maxLen; i++) {
            r.push({ pn: pns[i] || '', desc: descs[i] || '', id: uid() });
        }
        setRows(r);
        setNm(initialData?.nounModifier || '');
        setSearch('');
        setSelected(new Set());
        setEditCell(null);
        setScrollTop(0);
        setNmSuggestions([]);
        setNmSuggestOpen(false);
        nextId.current = maxLen + 1;
    }, [open, initialData]);

    // ── NM Autocomplete: debounced suggest ──
    useEffect(() => {
        if (!open || editingId || !nm.trim()) {
            setNmSuggestions([]);
            setNmSuggestOpen(false);
            return;
        }
        const timer = setTimeout(async () => {
            setNmSuggestLoading(true);
            try {
                const resp = await apiClient.nounModifierSuggest(nm.trim());
                if (resp.success && resp.data?.length > 0) {
                    setNmSuggestions(resp.data);
                    setNmSuggestOpen(true);
                } else {
                    setNmSuggestions([]);
                    setNmSuggestOpen(false);
                }
            } catch (_) {
                setNmSuggestions([]);
            }
            setNmSuggestLoading(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [nm, open, editingId]);

    // ── Close suggest dropdown on outside click ──
    useEffect(() => {
        if (!nmSuggestOpen) return;
        const handler = (e) => {
            if (suggestDropdownRef.current && !suggestDropdownRef.current.contains(e.target) &&
                nmInputRef.current && !nmInputRef.current.contains(e.target)) {
                setNmSuggestOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [nmSuggestOpen]);

    // ── Handle selecting a suggestion → load existing group data ──
    const handleSelectSuggestion = async (suggestion) => {
        setNmSuggestOpen(false);
        setNmSuggestions([]);
        setNm(suggestion.noun_modifier);
        try {
            const resp = await apiClient.nounModifierGetById(suggestion.id);
            if (resp.success && resp.data) {
                const group = resp.data;
                const items = group.items || [];
                const loadedRows = items.map(it => ({
                    pn: it.part_number || '',
                    desc: it.description || '',
                    id: uid()
                }));
                setRows(loadedRows);
                nextId.current = loadedRows.length + 1;
                // Tell parent to switch to edit mode for this group
                if (onEditIdChange) onEditIdChange(suggestion.id);
                toast.info(`Loaded "${suggestion.noun_modifier}" with ${items.length} existing item(s). Add more or edit, then save.`);
            }
        } catch (err) {
            toast.error('Failed to load existing group: ' + err.message);
        }
    };

    // ── Keyboard shortcuts ──
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') { if (editCell) setEditCell(null); else onClose(); }
            if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, editCell]);

    // ── Derived data ──
    const filtered = useMemo(() => {
        let list = rows.map((r, i) => ({ ...r, _origIdx: i }));
        if (search.trim()) {
            const s = search.toUpperCase();
            list = list.filter(r => r.pn.includes(s) || r.desc.includes(s));
        }
        if (sortCol) {
            const key = sortCol === 'pn' ? 'pn' : 'desc';
            const dir = sortDir === 'asc' ? 1 : -1;
            list.sort((a, b) => a[key].localeCompare(b[key]) * dir);
        }
        return list;
    }, [rows, search, sortCol, sortDir]);

    // ── Stats ──
    const stats = useMemo(() => {
        const pnSet = new Set();
        let empty = 0, dupCount = 0;
        rows.forEach(r => {
            if (!r.pn.trim() && !r.desc.trim()) empty++;
            if (r.pn.trim()) {
                if (pnSet.has(r.pn)) dupCount++;
                pnSet.add(r.pn);
            }
        });
        return { total: rows.length, pns: pnSet.size, descs: rows.filter(r => r.desc.trim()).length, dups: dupCount, empty };
    }, [rows]);

    // ── Virtual scroll ──
    const totalHeight = filtered.length * VIRTUAL_ROW_H;
    const containerH = scrollRef.current?.clientHeight || 600;
    const startIdx = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_H) - VISIBLE_BUFFER);
    const endIdx = Math.min(filtered.length, Math.ceil((scrollTop + containerH) / VIRTUAL_ROW_H) + VISIBLE_BUFFER);
    const visibleRows = filtered.slice(startIdx, endIdx);

    // ── Row operations ──
    const addRow = () => {
        setRows(r => [...r, { pn: '', desc: '', id: uid() }]);
        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = totalHeight + 100; }, 50);
    };

    const updateRow = (origIdx, field, value) => {
        setRows(r => r.map((row, i) => i === origIdx ? { ...row, [field]: value.toUpperCase() } : row));
    };

    const deleteRows = (indices) => {
        const idxSet = new Set(indices);
        setRows(r => r.filter((_, i) => !idxSet.has(i)));
        setSelected(new Set());
        toast.success(`Deleted ${indices.length} row(s)`);
    };

    const deleteSelected = () => {
        if (selected.size === 0) return;
        const ok = window.confirm(`Delete ${selected.size} selected row(s)?`);
        if (ok) deleteRows([...selected]);
    };

    // ── Inline edit ──
    const startEdit = (idx, field, currentVal) => {
        setEditCell({ idx, field });
        setEditVal(currentVal);
        setTimeout(() => editRef.current?.focus(), 30);
    };

    const commitEdit = () => {
        if (editCell) {
            updateRow(editCell.idx, editCell.field, editVal);
            setEditCell(null);
        }
    };

    const handleEditKey = (e) => {
        if (e.key === 'Enter') { commitEdit(); }
        if (e.key === 'Escape') { setEditCell(null); }
        if (e.key === 'Tab') {
            e.preventDefault();
            commitEdit();
            if (editCell.field === 'pn') {
                startEdit(editCell.idx, 'desc', rows[editCell.idx]?.desc || '');
            } else {
                const next = editCell.idx + 1;
                if (next < rows.length) startEdit(next, 'pn', rows[next]?.pn || '');
            }
        }
    };

    // ── Bulk paste ──
    const handleBulkPaste = (e) => {
        const text = e.clipboardData?.getData('text') || '';
        const lines = text.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);
        if (lines.length === 0) return;
        e.preventDefault();
        const newRows = lines.map(line => {
            const parts = line.split(/[\t,]/).map(s => s.trim().toUpperCase());
            return { pn: parts[0] || '', desc: parts[1] || '', id: uid() };
        });
        setRows(r => [...r, ...newRows]);
        toast.success(`Pasted ${newRows.length} row(s)`);
    };

    // ── Bulk import file ──
    const fileRef = useRef(null);
    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const ext = file.name.split('.').pop().toLowerCase();
            let imported = [];
            if (ext === 'xlsx' || ext === 'xls') {
                const data = await file.arrayBuffer();
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                for (let i = 1; i < raw.length; i++) {
                    const pn = (raw[i][0] || '').toString().trim().toUpperCase();
                    const desc = (raw[i][1] || '').toString().trim().toUpperCase();
                    if (pn || desc) imported.push({ pn, desc, id: uid() });
                }
            } else {
                const text = await file.text();
                text.split(/[\n\r]+/).filter(Boolean).slice(1).forEach(line => {
                    const parts = line.split(/[\t,]/).map(s => s.trim().toUpperCase());
                    if (parts[0] || parts[1]) imported.push({ pn: parts[0] || '', desc: parts[1] || '', id: uid() });
                });
            }
            setRows(r => [...r, ...imported]);
            toast.success(`Imported ${imported.length} row(s)`);
        } catch (err) { toast.error('Import failed: ' + err.message); }
        finally { e.target.value = ''; }
    };

    // ── Export ──
    const handleExport = () => {
        const wsData = [['Part Number', 'Description']];
        rows.forEach(r => wsData.push([r.pn, r.desc]));
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 30 }, { wch: 60 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, `nm_${nm || 'record'}_${Date.now()}.xlsx`);
    };

    // ── Select ──
    const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r._origIdx));
    const toggleSelectAll = () => {
        if (allFilteredSelected) setSelected(new Set());
        else setSelected(new Set(filtered.map(r => r._origIdx)));
    };
    const toggleSelect = (origIdx) => {
        setSelected(prev => { const n = new Set(prev); n.has(origIdx) ? n.delete(origIdx) : n.add(origIdx); return n; });
    };

    // ── Sort ──
    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    // ── Save ──
    const handleSave = async () => {
        const validRows = rows.filter(r => r.pn.trim());
        if (validRows.length === 0) { toast.error('At least one Part Number is required'); return; }
        if (!nm.trim()) { toast.error('Noun/Modifier is required'); return; }
        setSaving(true);
        try {
            await onSave({
                partNumbers: validRows.map(r => r.pn.trim()),
                descriptions: validRows.map(r => r.desc.trim()),
                nounModifier: nm.trim()
            });
        } finally { setSaving(false); }
    };

    if (!open) return null;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 text-slate-400" />;
        return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-500" /> : <ArrowDown className="w-3 h-3 text-blue-500" />;
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#f1f5f9' }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={handleImportFile} />

            {/* ══ TOP BAR ══ */}
            <div className="shrink-0 flex items-center justify-between px-6 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: editingId ? '#fef3c7' : '#d1fae5' }}>
                        {editingId ? <Pencil className="w-4 h-4 text-amber-600" /> : <Plus className="w-4 h-4 text-emerald-600" />}
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            {editingId ? 'Edit Record' : 'Add New Record'}
                            {editingId && <span className="text-slate-400 font-normal text-xs">#{editingId}</span>}
                        </h2>
                        <p className="text-[10px] text-slate-400">Ctrl+S to save · Esc to close · Tab to navigate</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* ══ NM + STATS BAR ══ */}
            <div className="shrink-0 px-6 py-3 flex flex-wrap items-center gap-4" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md relative">
                    <Tag className="w-4 h-4 text-violet-500 shrink-0" />
                    <div className="relative flex-1">
                        <input ref={nmInputRef} type="text" value={nm}
                            onChange={e => setNm(e.target.value.toUpperCase())}
                            onFocus={() => { if (nmSuggestions.length > 0 && !editingId) setNmSuggestOpen(true); }}
                            placeholder="NOUN/MODIFIER * (type to search existing)" autoComplete="off"
                            className="w-full h-9 px-3 rounded-lg text-sm font-bold text-slate-800 placeholder:text-slate-400 bg-slate-100 border border-slate-200 outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/30 transition" />
                        {nmSuggestLoading && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loader2 className="w-3.5 h-3.5 text-violet-400 animate-spin" />
                            </div>
                        )}
                        {/* Autocomplete dropdown */}
                        {nmSuggestOpen && nmSuggestions.length > 0 && (
                            <div ref={suggestDropdownRef}
                                className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-60 overflow-y-auto"
                                style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.12)' }}>
                                <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                                    Existing Groups — click to load & edit
                                </div>
                                {nmSuggestions.map(s => (
                                    <button key={s.id}
                                        onClick={() => handleSelectSuggestion(s)}
                                        className="w-full text-left px-3 py-2.5 hover:bg-violet-50 transition flex items-center justify-between gap-3 border-b border-slate-50 last:border-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Tag className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                                            <span className="text-sm font-bold text-slate-800 truncate">{s.noun_modifier}</span>
                                        </div>
                                        <span className="shrink-0 text-[10px] font-semibold text-violet-500 bg-violet-100 px-2 py-0.5 rounded-full">
                                            {Number(s.item_count)} item{Number(s.item_count) !== 1 ? 's' : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {/* Stat pills */}
                {[
                    { label: 'Rows', val: stats.total, color: '#3b82f6' },
                    { label: 'PNs', val: stats.pns, color: '#8b5cf6' },
                    { label: 'Descs', val: stats.descs, color: '#14b8a6' },
                    { label: 'Dups', val: stats.dups, color: stats.dups > 0 ? '#ef4444' : '#64748b' },
                    { label: 'Empty', val: stats.empty, color: stats.empty > 0 ? '#f59e0b' : '#64748b' },
                ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: s.color + '15', border: `1px solid ${s.color}30` }}>
                        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: s.color + 'cc' }}>{s.label}</span>
                        <span className="text-xs font-bold text-slate-800">{s.val.toLocaleString()}</span>
                    </div>
                ))}
            </div>

            {/* ══ TOOLBAR ══ */}
            <div className="shrink-0 px-6 py-2.5 flex flex-wrap items-center gap-2" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {/* Search */}
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search rows..."
                        className="w-full h-8 pl-8 pr-3 rounded-lg text-xs text-slate-700 bg-white border border-slate-200 outline-none focus:border-blue-500 transition placeholder:text-slate-400" />
                    {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>}
                </div>

                <div className="h-6 w-px bg-slate-200" />

                {/* Add row */}
                <button onClick={addRow} className="h-8 px-3 rounded-lg text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Add Row
                </button>
                {/* Import */}
                <button onClick={() => fileRef.current?.click()} className="h-8 px-3 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" /> Import
                </button>
                {/* Export */}
                <button onClick={handleExport} disabled={rows.length === 0} className="h-8 px-3 rounded-lg text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 transition flex items-center gap-1.5 disabled:opacity-30">
                    <Download className="w-3.5 h-3.5" /> Export
                </button>

                {selected.size > 0 && (
                    <>
                        <div className="h-6 w-px bg-slate-200" />
                        <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">{selected.size} selected</span>
                        <button onClick={deleteSelected} className="h-8 px-3 rounded-lg text-xs font-semibold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 transition flex items-center gap-1.5">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                        <button onClick={() => setSelected(new Set())} className="text-[10px] text-slate-400 hover:text-slate-700">Clear</button>
                    </>
                )}

                <div className="flex-1" />
                <span className="text-[10px] text-slate-500">{filtered.length === rows.length ? `${rows.length.toLocaleString()} rows` : `${filtered.length.toLocaleString()} / ${rows.length.toLocaleString()}`}</span>
            </div>

            {/* ══ TABLE HEADER ══ */}
            <div className="shrink-0 flex items-center px-6" style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', height: '36px' }}>
                <div className="w-10 shrink-0 flex items-center justify-center">
                    <button onClick={toggleSelectAll} className="text-slate-400 hover:text-blue-600 transition">
                        {allFilteredSelected && filtered.length > 0 ? <CheckSquare className="w-3.5 h-3.5 text-blue-600" /> : <Square className="w-3.5 h-3.5" />}
                    </button>
                </div>
                <div className="w-14 shrink-0 text-[10px] font-bold text-slate-500 uppercase tracking-wider">#</div>
                <div className="flex-1 min-w-0">
                    <button onClick={() => handleSort('pn')} className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:text-slate-800 transition">
                        Part Number <SortIcon col="pn" />
                    </button>
                </div>
                <div className="flex-[1.5] min-w-0">
                    <button onClick={() => handleSort('desc')} className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:text-slate-800 transition">
                        Description <SortIcon col="desc" />
                    </button>
                </div>
                <div className="w-16 shrink-0 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Act</div>
            </div>

            {/* ══ TABLE BODY — virtual scroll ══ */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden" onScroll={e => setScrollTop(e.target.scrollTop)}
                onPaste={!editCell ? handleBulkPaste : undefined} tabIndex={-1}>
                <div style={{ height: totalHeight, position: 'relative' }}>
                    {visibleRows.map((row) => {
                        const isSelected = selected.has(row._origIdx);
                        const isDup = rows.filter(r => r.pn && r.pn === row.pn).length > 1 && row.pn;
                        const isEmpty = !row.pn.trim() && !row.desc.trim();
                        const yPos = (startIdx + visibleRows.indexOf(row)) * VIRTUAL_ROW_H;

                        return (
                            <div key={row.id} className={`absolute left-0 right-0 flex items-center px-6 border-b transition-colors ${isSelected ? 'bg-blue-50 border-blue-200' : isEmpty ? 'bg-amber-50/60 border-slate-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}
                                style={{ top: yPos, height: VIRTUAL_ROW_H }}>
                                {/* Checkbox */}
                                <div className="w-10 shrink-0 flex items-center justify-center">
                                    <button onClick={() => toggleSelect(row._origIdx)} className="text-slate-400 hover:text-blue-600 transition">
                                        {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-blue-600" /> : <Square className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                                {/* Row # */}
                                <div className="w-14 shrink-0 text-[10px] text-slate-400 font-mono">{row._origIdx + 1}</div>
                                {/* PN */}
                                <div className="flex-1 min-w-0 pr-2">
                                    {editCell?.idx === row._origIdx && editCell?.field === 'pn' ? (
                                        <input ref={editRef} value={editVal} onChange={e => setEditVal(e.target.value.toUpperCase())}
                                            onBlur={commitEdit} onKeyDown={handleEditKey}
                                            className="w-full h-7 px-2 rounded text-xs font-mono text-slate-800 bg-white border border-blue-500 outline-none ring-2 ring-blue-100" />
                                    ) : (
                                        <div onClick={() => startEdit(row._origIdx, 'pn', row.pn)}
                                            className={`flex items-center gap-1.5 h-7 px-2 rounded cursor-text group ${row.pn ? 'hover:bg-slate-100' : 'hover:bg-slate-50'}`}>
                                            <code className={`text-xs font-mono truncate ${row.pn ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                                                {row.pn || 'Click to add...'}
                                            </code>
                                            {isDup && <span className="shrink-0 text-[8px] font-bold text-red-600 bg-red-100 px-1 rounded">DUP</span>}
                                            <Pencil className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 shrink-0" />
                                        </div>
                                    )}
                                </div>
                                {/* Description */}
                                <div className="flex-[1.5] min-w-0 pr-2">
                                    {editCell?.idx === row._origIdx && editCell?.field === 'desc' ? (
                                        <input ref={editRef} value={editVal} onChange={e => setEditVal(e.target.value.toUpperCase())}
                                            onBlur={commitEdit} onKeyDown={handleEditKey}
                                            className="w-full h-7 px-2 rounded text-xs text-slate-800 bg-white border border-blue-500 outline-none ring-2 ring-blue-100" />
                                    ) : (
                                        <div onClick={() => startEdit(row._origIdx, 'desc', row.desc)}
                                            className={`flex items-center gap-1 h-7 px-2 rounded cursor-text group ${row.desc ? 'hover:bg-slate-100' : 'hover:bg-slate-50'}`}>
                                            <span className={`text-xs truncate ${row.desc ? 'text-slate-600' : 'text-slate-400 italic'}`}>
                                                {row.desc || 'Click to add...'}
                                            </span>
                                            <Pencil className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 shrink-0" />
                                        </div>
                                    )}
                                </div>
                                {/* Actions */}
                                <div className="w-16 shrink-0 flex items-center justify-center">
                                    <button onClick={() => deleteRows([row._origIdx])}
                                        className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <FileText className="w-10 h-10 mb-3 text-slate-300" />
                        <p className="text-sm font-medium text-slate-500">{search ? 'No rows match your search' : 'No data yet'}</p>
                        <p className="text-xs text-slate-400 mt-1">{search ? 'Try a different search term' : 'Click "Add Row" or paste data (Ctrl+V) to get started'}</p>
                    </div>
                )}
            </div>

            {/* ══ BOTTOM BAR ══ */}
            <div className="shrink-0 flex items-center justify-between px-6 py-3" style={{ background: '#ffffff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -1px 3px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                    <span>💡 Click any cell to edit · Paste CSV/Excel data directly · Tab to navigate</span>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" onClick={onClose}
                        className="px-5 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSave}
                        disabled={saving || rows.filter(r => r.pn.trim()).length === 0 || !nm.trim()}
                        className="inline-flex items-center gap-2 px-6 py-2 text-sm font-bold text-white rounded-xl transition shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: saving ? '#475569' : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {editingId ? 'Save Changes' : `Add ${rows.filter(r => r.pn.trim()).length.toLocaleString()} Record(s)`}
                    </button>
                </div>
            </div>
        </div>
    );
}
